import { cn } from "@/lib/utils";
import * as Toast from "@radix-ui/react-toast";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { useContext, createContext, useState, JSX } from "react";

interface ShowToastOptions {
  title: string;
  description?: string;
  type?: "SUCCESS" | "ERROR" | "INFO" | "WARNING";
}

type ToastContextType = {
  showToast: (options: ShowToastOptions) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_ICON: {
  [key: string]: JSX.Element;
} = {
  SUCCESS: <CircleCheck size={16} />,
  ERROR: <CircleX size={16} />,
  INFO: <Info size={16} />,
  WARNING: <TriangleAlert size={16} />,
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("SUCCESS");

  const showToast = ({
    title,
    description,
    type = "SUCCESS",
  }: ShowToastOptions) => {
    setTitle(title);
    setDescription(description || "");
    setType(type);

    // Open the toast immediately
    setOpen(true);

    // Automatically close after 3000ms (3 seconds)
    setTimeout(() => setOpen(false), 5000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toast.Provider swipeDirection="right" duration={100000}>
        {children}
        <Toast.Root
          open={open}  // controlled by state `open`
          onOpenChange={setOpen}  // allow user to manually close as well
          className={cn(
            "px-4 py-3 rounded-lg shadow-md border data-[state=open]:animate-slide-in data-[state=closed]:animate-slide-out",
            type === "SUCCESS" && "bg-green-400 text-green-900 border-green-700",
            type === "ERROR" && "bg-red-400 text-red-900 border-red-700",
            type === "INFO" && "bg-sky-400 text-sky-900 border-sky-700",
            type === "WARNING" && "bg-orange-400 text-orange-900 border-orange-700"
          )}
        >
          <Toast.Title className="font-semibold flex items-center gap-1">
            {TOAST_ICON[type]} {title}
          </Toast.Title>
          {description && (
            <Toast.Description className="text-sm opacity-80">
              {description}
            </Toast.Description>
          )}
        </Toast.Root>
        <Toast.Viewport className="fixed top-4 right-4 z-50 w-96 max-w-full outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
};
